import React from 'react'
import { Credibility } from '@chegg-wt/core/types/core'

const credibilityHeader = `Is this source credible? Consider the criteria below.`

const considerCredibility: Credibility = {
  header: credibilityHeader,
  body: [
    {
      header: 'Purpose',
      body: [
        <>
          Is the purpose to <b>entertain, sell, persuade, or inform/teach</b>?<br />
          <>
            <br />
            Journal articles are often designed to inform or teach. Books and websites
            could have any of these or a combination of the purposes above. So it is
            important to determine why the source was created and if it is appropriate for
            your research. For websites in particular, looking at their &quot;About
            Us&quot; page or &quot;Mission Statement&quot; can help you evaluate purpose.
          </>
        </>,
      ],
      isList: false,
    },
    {
      header: 'Accuracy',
      body: [
        <>
          Accuracy is the reliability and truthfulness of the source. Here are a few
          indicators of an accurate source:
          <br />
          <br />
        </>,
      ],
      isList: false,
    },
    {
      body: [
        'Citations or a works cited list. For websites, this can be links to other credible sites.',
        'Evidence that backs up claims made by the author(s).',
        'Text that is free of spelling and grammatical errors.',
        'Information that matches that in other, credible sources.',
        'Language that is unbiased and free of emotion.',
      ],
      tabbed: true,
    },
    {
      body: [
        <>
          <br />
          Based on the above the source could be accurate, inaccurate, a mixture of
          accurate and inaccurate, or hard to tell.
        </>,
      ],
      isList: false,
    },
    {
      header: 'Authority: Author',
      body: [
        <>
          The author is the individual or organization who wrote the information in the
          book, in the journal article, or on the website. If no author is listed, there
          may be another contributor instead. For example, an editor or a translator. A
          credible author has:
          <br />
          <br />
        </>,
      ],
      isList: false,
    },
    {
      body: [
        'Written several articles or books on the topic.',
        'Provided contact information. For example, an email address, mailing address, social media account, etc.',
        'The experience or qualifications to be an expert on the topic.',
      ],
      tabbed: true,
    },
    {
      header: 'Authority: Publisher',
      body: [
        <>
          The credibility of the publisher can contribute to the authority of a source.
          The publisher can be a person, company or organization. Authoritative
          publishers:
          <br />
          <br />
        </>,
      ],
      isList: false,
    },
    {
      body: [
        'Accept responsibility for content.',
        'Are often well-known.',
        'Often publish multiple works on the same or related topics.',
      ],
      tabbed: true,
    },
    {
      header: 'Relevance',
      body: [
        <>
          Relevance describes how related or important a source is to your topic. While a
          source may be credible, it does not necessarily mean it is relevant to your
          assignment. To determine relevance, you should:
          <br />
          <br />
        </>,
      ],
      isList: false,
    },
    {
      body: [
        "Determine the website's intended audience. Look at the level of the information and the tone of the writing. For example, is it meant for academics or the general public?",
        'Make sure that the information is related to your research topic.',
        'Make sure that the information helps you answer your research question.',
      ],
      tabbed: true,
    },
    {
      header: 'Currency',
      body: [
        <>
          A publication date is an important part of evaluating the credibility of a
          source and its appropriateness for your topic. It is generally best to use
          content that was recently published or updated, but depending on your
          assignment, it may be appropriate to use older information.
          <br />
          <br />
          For example, a journal entry from Abraham Lincoln during the Civil War is too
          outdated to use in a discussion about modern politics and war, but would be
          appropriate for a paper about the Civil War. Consider the following when
          evaluating currency:
          <br />
          <br />
        </>,
      ],
      isList: false,
    },
    {
      body: [
        'Was it published or updated recently? If a website, is there even a publication date listed?',
        'Is the date of the source appropriate or inappropriate for my assignment?',
      ],
      tabbed: true,
    },
    {
      header: 'Review',
      body: [
        'After analyzing your source, do you believe it is credible, not credible, partially credible, or are you unsure? If you are still unsure, it may help to ask your instructor a librarian for assistance.',
      ],
      isList: false,
    },
  ],
}

export const findPageCredibility = {
  header: 'Is your source credible? Don’t forget to consider these factors.',
  body: [
    {
      header: 'Purpose : Reason the source exists',
      body: [
        'Is the point of the information to inform, persuade, teach, or sell?',
        'Do the authors/publishers make their intentions clear?',
        'Does the information appear to be fact or opinion?',
        'Does the point of view seem impartial? Do they identify counter-arguments?',
      ],
    },
    {
      header: 'Authority - Author: Source of the information',
      body: [
        'Who is the author? What are their credentials or qualifications?',
        'What makes the author qualified to write on this topic?',
        'Is there clearly defined contact information for the author?',
      ],
    },
    {
      header: 'Authority - Publisher: Source of the information',
      body: [
        'Who is the publisher? Is it a non-profit, government agency, or organization? How might this affect their point of view?',
        'What makes the publisher qualified to generate works on this subject?',
        'What can the URL tell you about the publisher? For instance, .gov may signify that it is a government agency.',
      ],
    },
    {
      header: 'Relevance : Importance of the information to your topic',
      body: [
        'Who is the publisher? Is it a non-profit, government agency, or organization? How might this affect their point of view?',
        'What makes the publisher qualified to generate works on this subject?',
        'What can the URL tell you about the publisher? For instance, .gov may signify that it is a government agency.',
      ],
    },
    {
      header: 'Currency : Timeliness of the information',
      body: [
        'When was the information published? When was it last updated? Does it reflect the most current information available?',
        'How does your topic fit in with this source’s publication date? Do you need current information to make your point or do older sources work better?',
      ],
    },
    {
      header: 'Comprehensiveness',
      body: [
        'Does the source present one or multiple viewpoints on your topic?',
        'Does the source present a large amount of information on the topic? Or is it short and focused?',
        'Are there any points you feel may have been left out, on purpose or accidentally, that affect its comprehensiveness?',
      ],
    },
  ],
}

export default considerCredibility
